import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Typography
} from '@mui/material';
import { Gear, Plus, Trash } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { IChatbotFlowModel } from '../../../data/models/chatbot';
import { IFlowModel } from '../../../data/models/flow';
import { FlowService } from '../../../service/FlowService';
import { useNotificationStore } from '../../../store/storeNotifications';
import { MuiInputControlled } from '../../Form/Input';
import { MuiRadioControlled } from '../../Form/Radio';
import { MuiSelectControlled } from '../../Form/Select';

const NameAndSetupSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
    firstMessage: z.string(),
    placeholder: z.string().optional(),
    storeOpeningMessage: z.string().optional(),
    chatbotDataInputNodeId: z.string().min(1, 'Data input node is required'),
    chatbotDataOutputNodeId: z.string().min(1, 'Data output node is required'),
    actions: z.array(
      z.object({
        name: z.string().min(1, 'Action title is required'),
        description: z.string().min(1, 'Action description is required'),
      }),
    ),
  })
  .refine(
    data => {
      if (data.actions && data.actions.length > 0) {
        return !!data.firstMessage;
      }

      return true;
    },
    {
      message: 'First message is required when there are actions',
      path: ['firstMessage'],
    },
  )

export type INameAndSetup = z.infer<typeof NameAndSetupSchema>;

interface NameAndSetupProps {
  chatData: IChatbotFlowModel;
  onSetFlow: any;
}

export function NameAndSetup({ chatData, onSetFlow }: NameAndSetupProps) {
  const setNotification = useNotificationStore(state => state.setNotification);

  const formsMethods = useForm<INameAndSetup>({
    resolver: zodResolver(NameAndSetupSchema),
  });

  const [flow, setFlow] = useState<IFlowModel>({} as IFlowModel);
 
  const { fields, append, remove } = useFieldArray({
    control: formsMethods.control,
    name: 'actions',
  });

  function handleAddAction() {
    if (fields.length >= 10) {
      setNotification({
        type: 'warning',
        message: 'Maximum of 10 actions allowed',
      });
      return;
    }

    append({
      name: '',
      description: '',
    });
  }

  function handleRemoveAction(index: number) {
    remove(index);
  }

  async function handleSubmitForms(data: INameAndSetup) {
    try {
      const response = await FlowService.updateFlow(chatData.id, {
        name: data.name,
        chatDescription: data.description,
        chatActions: data.actions,
        firstMessage: data.firstMessage === '' ? null : data.firstMessage,
        placeholder: data.placeholder === '' ? null : data.placeholder,
        storeOpeningMessage: data.storeOpeningMessage,
        chatbotDataInputNodeId: data.chatbotDataInputNodeId,
        chatbotDataOutputNodeId: data.chatbotDataOutputNodeId,
      });

      onSetFlow(response.data);

      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      console.error(error);
      setNotification({
        type: 'error',
        message: 'Error saving name and setup form',
      });
    }
  }

  useEffect(() => {
    async function getFlow() {
      const flow = await FlowService.getFlow(chatData.id);
      setFlow(flow.data);
    }

    getFlow();
  }, [chatData.id]);

  const dataInputNodes = useMemo(() => {
    const dataInputNodes =
      flow.nodes?.filter(node => node.type === 'varInputNode') ?? [];

    return dataInputNodes;
  }, [flow]);

  const dataOutputNodes = useMemo(() => {
    const dataOutputNodes =
      flow.nodes?.filter(node => node.type === 'outputObjectNode') ?? [];

    return dataOutputNodes;
  }, [flow]);

  useEffect(() => {
    if (chatData) {
      let dataInputNodes = flow.nodes?.filter(
        node => node.type === 'varInputNode',
      ) ?? [{ id: '' }];
      let dataOutputNodes = flow.nodes?.filter(
        node => node.type === 'outputObjectNode',
      ) ?? [{ id: '' }];

      formsMethods.reset(
        {
          name: chatData.name,
          description: chatData.chatDescription ?? '',
          firstMessage: chatData.firstMessage ?? '',
          placeholder: chatData.placeholder ?? '',
          storeOpeningMessage: chatData.storeOpeningMessage ?? 'in-chat',
          actions: chatData.chatActions ?? [],
          chatbotDataInputNodeId:
            chatData.chatbotDataInputNodeId ?? dataInputNodes[0].id,
          chatbotDataOutputNodeId:
            chatData.chatbotDataOutputNodeId ?? dataOutputNodes[0].id,
        },
        { keepDefaultValues: true },
      );
    }
  }, [chatData, formsMethods, flow.nodes]);

  return (
    <FormProvider {...formsMethods}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="16px"
        width="100%"
      >
        <Box display="flex" flexDirection="column" gap="16px">
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            width="100%"
            mb="8px"
          >
            <Gear size={20} color="#101828" />
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Name and setup
            </Typography>
          </Box>

          <form
            id="name-and-setup"
            onSubmit={formsMethods.handleSubmit(handleSubmitForms)}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <MuiInputControlled
              inputProps={{
                name: 'name',
                label: 'Add name',
                placeholder: 'Type your Fluxbot name here',
              }}
              styles={{ width: '318px' }}
            />

            <MuiInputControlled
              inputProps={{
                name: 'description',
                label: 'Add description',
                placeholder: 'Type your Fluxbot description here',
              }}
              styles={{ width: '318px' }}
            />

            <Divider />

            <Box display="flex" gap="24px" alignItems="center">
              <Box display="flex" flexDirection="column" gap="8px">
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#101828',
                  }}
                >
                  Opening message
                </Typography>

                <MuiInputControlled
                  inputProps={{
                    name: 'firstMessage',
                    label: 'Add Message',
                    placeholder: 'Hello how can I help you?',
                  }}
                  styles={{ width: '320px' }}
                />
              </Box>

              <Divider />

              <MuiRadioControlled
                inputProps={{
                  name: 'storeOpeningMessage',
                  label: 'Store opening message',
                  row: true,
                }}
                options={[
                  { value: 'in-chat', label: 'In chat' },
                  { value: 'as-title', label: 'As Title' },
                ]}
              />
            </Box>

            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Actions
            </Typography>

            {fields.map((field, index) => (
              <Box
                key={field.id}
                width="100%"
                display="flex"
                justifyContent="space-between"
                gap="4px"
              >
                <MuiInputControlled
                  inputProps={{
                    name: `actions[${index}].name`,
                    label: `Add Label`,
                    placeholder: 'Find me a project',
                  }}
                  styles={{ width: '300px' }}
                />

                <MuiInputControlled
                  inputProps={{
                    name: `actions[${index}].description`,
                    label: `System Prompt`,
                    placeholder:
                      'Start fresh by setting up a new project tailored to your needs. Choose your goals and get started in just.',
                  }}
                  styles={{ width: '100%' }}
                />

                <Button
                  onClick={() => handleRemoveAction(index)}
                  variant="text"
                >
                  <Trash size={20} color="red" />
                </Button>
              </Box>
            ))}

            <Box display="flex" justifyContent="end" width="100%">
              <Button
                variant="outlined"
                sx={{
                  width: '160px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onClick={handleAddAction}
                disabled={fields.length >= 10}
              >
                Add new <Plus size={16} />
              </Button>
            </Box>

            <Divider />

            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#101828',
              }}
            >
              Placeholder Text
            </Typography>

            <MuiInputControlled
              inputProps={{
                name: 'placeholder',
                label: 'Add placeholder',
                placeholder: 'Type your Fluxbot description here',
              }}
              styles={{ width: '100%' }}
            />

            <Divider />

            {/* Select Input and Output nodes */}
            <Box
              width="100%"
              display="flex"
              gap="16px"
              alignItems="start"
              flexDirection="column"
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#101828',
                }}
              >
                Select Input and Output nodes
              </Typography>

              <Box display="flex" alignItems="center" gap="8px">
                <MuiSelectControlled
                  styles={{ minWidth: '200px' }}
                  selectProps={{
                    name: 'chatbotDataInputNodeId',
                    label: 'Data input',
                    placeholder: 'Select a data input node',
                  }}
                >
                  {dataInputNodes.length === 0 && (
                    <MenuItem value="" disabled>
                      No data input nodes found
                    </MenuItem>
                  )}

                  {dataInputNodes.map(node => (
                    <MenuItem key={node.id} value={node.id}>
                      {node.data.label}
                    </MenuItem>
                  ))}
                </MuiSelectControlled>

                <MuiSelectControlled
                  styles={{ minWidth: '200px' }}
                  selectProps={{
                    name: 'chatbotDataOutputNodeId',
                    label: 'Data output',
                    placeholder: 'Select a data output node',
                  }}
                >
                  {dataOutputNodes.length === 0 && (
                    <MenuItem value="" disabled>
                      No data output nodes found
                    </MenuItem>
                  )}

                  {dataOutputNodes.map(node => (
                    <MenuItem key={node.id} value={node.id}>
                      {node.data.label}
                    </MenuItem>
                  ))}
                </MuiSelectControlled>
              </Box>
            </Box>
          </form>
        </Box>

        <Box display="flex" justifyContent="end" width="100%">
          <Button
            form="name-and-setup"
            type="submit"
            variant="contained"
            sx={{ width: '160px' }}
          >
            Save changes
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
}
